// AI 토큰 사용량 백오피스 — 기간별 조직 전체 사용량과 사용자 랭킹 (관리자 전용)
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AXEmptyState, AXSearchInput, AXSegmentedControl } from 'yukimushi-component-fe';
import { fetchUsageSummary, fetchUsageUsers, searchUsageUsers } from '../../api/adminUsage';
import type { UsageError, UsageSummary, UsageUser } from '../../api/adminUsage';
import { StatCard, UsageDailyChart, UsageKindBreakdown } from './UsageCharts';
import UserDetailPanel from './UserDetailPanel';
import {
  RANGE_PRESETS,
  REQUEST_COUNT_HINT,
  REQUEST_COUNT_LABEL,
  TOKEN_COLOR,
  formatDateTime,
  formatNumber,
  formatRelativeTime,
  isSystemUser,
  isValidRange,
  matchedPreset,
  presetRange,
  todayInKst,
  userLabel,
} from './usageFormat';
import styles from './AiUsage.module.css';

/** 랭킹에 받아올 인원 — 서버 상한은 500이다 */
const USER_LIMIT = 50;
/** 검색 입력이 멈춘 뒤 요청까지 기다리는 시간 */
const SEARCH_DEBOUNCE_MS = 300;

type SortKey = 'idNo' | 'dept' | 'totalTokens' | 'promptTokens' | 'completionTokens' | 'requestCount' | 'lastUsedAt';
type SortDir = 'asc' | 'desc';

/** 열마다 처음 눌렀을 때의 방향 — 수치는 큰 값부터, 이름은 가나다순이 자연스럽다 */
const DEFAULT_DIR: Record<SortKey, SortDir> = {
  idNo: 'asc',
  dept: 'asc',
  totalTokens: 'desc',
  promptTokens: 'desc',
  completionTokens: 'desc',
  requestCount: 'desc',
  lastUsedAt: 'desc',
};

function compareUsers(a: UsageUser, b: UsageUser, key: SortKey): number {
  if (key === 'idNo') return (a.username || a.userId).localeCompare(b.username || b.userId, 'ko');
  if (key === 'dept') return (a.deptCode || '').localeCompare(b.deptCode || '', 'ko');
  if (key === 'lastUsedAt') {
    // 기록이 없는 사용자는 0으로 둬서 기본(내림차순)에서 뒤로 밀린다
    return (Date.parse(a.lastUsedAt || '') || 0) - (Date.parse(b.lastUsedAt || '') || 0);
  }
  return a[key] - b[key];
}

interface Selection {
  user: UsageUser;
  /** 랭킹에서 열었으면 true — 검색 결과는 수치가 전부 0이라 신원만 쓴다 */
  hasListValues: boolean;
}

export default function AiUsagePage() {
  const initial = useMemo(() => presetRange(30), []);
  /* 조회에 실제로 쓰는 기간과 날짜 입력창의 값을 분리한다 —
     직접 지정 중에 매 키 입력마다 요청이 나가지 않게 '조회'로만 반영한다 */
  const [range, setRange] = useState(initial);
  const [draftFrom, setDraftFrom] = useState(initial.from);
  const [draftTo, setDraftTo] = useState(initial.to);

  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [users, setUsers] = useState<UsageUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<UsageError | null>(null);

  // 서버는 총 토큰 내림차순으로만 주므로 초기 상태를 그것과 맞춰 둔다
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'totalTokens', dir: 'desc' });

  const [keyword, setKeyword] = useState('');
  const [searchResults, setSearchResults] = useState<UsageUser[] | null>(null);
  const [selection, setSelection] = useState<Selection | null>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);

  /* ── 요약 + 랭킹 조회 ── */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchUsageSummary(range.from, range.to),
      fetchUsageUsers(range.from, range.to, USER_LIMIT),
    ])
      .then(([summaryResult, userResult]) => {
        if (cancelled) return;
        setSummary(summaryResult);
        setUsers(userResult.users);
      })
      .catch((err: UsageError) => {
        if (cancelled) return;
        setSummary(null);
        setUsers([]);
        setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [range]);

  /* ── 사번·부서 검색 (신원 조회 전용) ── */
  useEffect(() => {
    const trimmed = keyword.trim();
    if (!trimmed) {
      setSearchResults(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      searchUsageUsers(trimmed)
        .then((results) => { if (!cancelled) setSearchResults(results); })
        .catch(() => { if (!cancelled) setSearchResults([]); });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [keyword]);

  /* 결과 목록은 본문을 덮으므로 바깥을 누르면 닫는다.
     검색 결과 항목도 searchBox 안이라 항목 클릭은 닫힘에 걸리지 않는다 */
  useEffect(() => {
    if (!searchResults) return undefined;
    const handleDown = (event: MouseEvent) => {
      const box = searchBoxRef.current;
      if (box && !box.contains(event.target as Node)) setSearchResults(null);
    };
    document.addEventListener('mousedown', handleDown);
    return () => document.removeEventListener('mousedown', handleDown);
  }, [searchResults]);

  const applyPreset = useCallback((days: number) => {
    const next = presetRange(days);
    setDraftFrom(next.from);
    setDraftTo(next.to);
    setRange(next);
  }, []);

  const applyDraft = useCallback(() => {
    if (!isValidRange(draftFrom, draftTo)) return;
    setRange({ from: draftFrom, to: draftTo });
  }, [draftFrom, draftTo]);

  const toggleSort = useCallback((key: SortKey) => {
    setSort((prev) => (prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: DEFAULT_DIR[key] }));
  }, []);

  const sortedUsers = useMemo(() => {
    const list = users.slice();
    list.sort((a, b) => (sort.dir === 'asc' ? 1 : -1) * compareUsers(a, b, sort.key));
    return list;
  }, [users, sort]);

  const activePreset = matchedPreset(range.from, range.to);
  const draftValid = isValidRange(draftFrom, draftTo);
  const today = todayInKst();

  /* DB 장애 시에도 에러가 아니라 빈 값이 온다 — 프론트에서 둘을 구분할 방법이 없으므로
     '데이터 없음'으로만 알리고, 기간을 함께 보여줘 사용자가 판단하게 한다 */
  const isEmpty = !!summary && summary.totals.requestCount === 0 && summary.daily.length === 0;

  const periodControls = (
    <div className={styles.periodBar}>
      <AXSegmentedControl
        size="sm"
        options={RANGE_PRESETS.map((preset) => ({ value: String(preset.days), label: preset.label }))}
        value={activePreset === null ? '' : String(activePreset)}
        onChange={(value) => applyPreset(Number(value))}
      />
      <div className={styles.dateGroup}>
        <input type="date" className={styles.dateInput} value={draftFrom} max={today}
               onChange={(event) => setDraftFrom(event.target.value)} aria-label="시작일" />
        <span className={styles.dateTilde}>~</span>
        <input type="date" className={styles.dateInput} value={draftTo} max={today}
               onChange={(event) => setDraftTo(event.target.value)} aria-label="종료일" />
        <button type="button" className={styles.applyButton} onClick={applyDraft} disabled={!draftValid}>
          조회
        </button>
      </div>
      {!draftValid && <span className={styles.dateError}>시작일이 종료일보다 뒤일 수 없습니다.</span>}
    </div>
  );

  const searchBox = (
    <div className={styles.searchBox} ref={searchBoxRef}>
      <AXSearchInput
        value={keyword}
        onChange={setKeyword}
        onClear={() => setKeyword('')}
        placeholder="사번·부서로 사용자 찾기"
      />
      {searchResults && (
        <div className={styles.searchResults}>
          {searchResults.length === 0 ? (
            <p className={styles.searchEmpty}>일치하는 사용자가 없습니다.</p>
          ) : (
            searchResults.map((user) => {
              const label = userLabel(user);
              return (
                <button
                  key={user.userId}
                  type="button"
                  className={styles.searchItem}
                  /* 검색 응답의 토큰 값은 전부 0이다 — 신원만 넘기고 수치는 상세 API가 다시 받는다 */
                  onClick={() => {
                    setSelection({ user, hasListValues: false });
                    setKeyword('');
                  }}
                >
                  <span className={styles.searchItemId}>{label.idNo}</span>
                  <span className={styles.searchItemDept}>{label.dept}</span>
                  {label.shortId && <span className={styles.searchItemSub}>{label.shortId}</span>}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );

  if (error && error.status === 403) {
    return (
      <div className={styles.page}>
        <div className={styles.centered}>
          <AXEmptyState
            title="권한이 없습니다"
            description="AI 사용량 백오피스는 관리자만 볼 수 있습니다. 접근이 필요하면 시스템 관리자에게 문의해 주세요."
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerTop}>
          <h1 className={styles.title}>AI 토큰 사용량</h1>
          {searchBox}
        </div>
        {periodControls}
      </header>

      <div className={styles.body}>
        <div className={styles.content}>
          {loading && <p className={styles.status}>불러오는 중…</p>}

          {!loading && error && (
            <div className={styles.centered}>
              <AXEmptyState title="조회에 실패했습니다" description={error.message} />
            </div>
          )}

          {!loading && !error && isEmpty && (
            <div className={styles.centered}>
              <AXEmptyState
                title="이 기간에는 사용 기록이 없습니다"
                description={`${range.from} ~ ${range.to} (KST) 구간에 집계된 값이 없습니다. 기간을 넓혀 다시 조회해 보세요.`}
              />
            </div>
          )}

          {!loading && !error && summary && !isEmpty && (
            <>
              <section className={styles.statRow}>
                <StatCard label="총 토큰" value={summary.totals.totalTokens} />
                <StatCard label="입력 토큰" value={summary.totals.promptTokens} accent={TOKEN_COLOR.prompt} />
                <StatCard label="출력 토큰" value={summary.totals.completionTokens} accent={TOKEN_COLOR.completion} />
                <StatCard label={REQUEST_COUNT_LABEL} value={summary.totals.requestCount} hint={REQUEST_COUNT_HINT} />
                <StatCard label="사용자 수" value={summary.totals.userCount} />
              </section>

              <section className={styles.chartRow}>
                <div className={styles.card}>
                  <h2 className={styles.cardTitle}>일별 추이</h2>
                  <UsageDailyChart rows={summary.daily} />
                </div>
                <div className={styles.card}>
                  <h2 className={styles.cardTitle}>호출 종류 비중</h2>
                  <UsageKindBreakdown models={summary.models} />
                </div>
              </section>

              <section className={styles.card}>
                <h2 className={styles.cardTitle}>사용자 랭킹</h2>
                <p className={styles.cardCaption}>
                  총 토큰 상위 {USER_LIMIT}명입니다. 다른 기준 정렬은 이 {USER_LIMIT}명 안에서만
                  이뤄집니다 — 조직 전체 순위가 아닙니다.
                </p>
                <UserRankingTable
                  users={sortedUsers}
                  sortKey={sort.key}
                  sortDir={sort.dir}
                  onSort={toggleSort}
                  selectedUserId={selection ? selection.user.userId : null}
                  onSelect={(user) => setSelection({ user, hasListValues: true })}
                />
              </section>
            </>
          )}
        </div>

        {selection && (
          <UserDetailPanel
            key={selection.user.userId}
            user={selection.user}
            hasListValues={selection.hasListValues}
            from={range.from}
            to={range.to}
            onClose={() => setSelection(null)}
          />
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
 * 랭킹 표 — 서버에 정렬 옵션이 없어 헤더 클릭 정렬은 클라이언트에서 한다
 * ──────────────────────────────────────────────────────────────── */

interface RankingProps {
  users: UsageUser[];
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  selectedUserId: string | null;
  onSelect: (user: UsageUser) => void;
}

const COLUMNS: { key: SortKey; label: string; numeric: boolean }[] = [
  { key: 'idNo', label: '사번', numeric: false },
  { key: 'dept', label: '부서', numeric: false },
  { key: 'totalTokens', label: '총 토큰', numeric: true },
  { key: 'promptTokens', label: '입력', numeric: true },
  { key: 'completionTokens', label: '출력', numeric: true },
  { key: 'requestCount', label: REQUEST_COUNT_LABEL, numeric: true },
  { key: 'lastUsedAt', label: '마지막 사용', numeric: false },
];

function UserRankingTable({ users, sortKey, sortDir, onSort, selectedUserId, onSelect }: RankingProps) {
  if (users.length === 0) {
    return <p className={styles.chartEmpty}>이 기간에 사용한 사용자가 없습니다.</p>;
  }

  return (
    <div className={styles.tableWrap}>
      <table className={styles.rankTable}>
        <thead>
          <tr>
            {COLUMNS.map((column) => (
              <th
                key={column.key}
                className={`${column.numeric ? styles.num : ''} ${sortKey === column.key ? styles.sorted : ''}`}
                onClick={() => onSort(column.key)}
                aria-sort={sortKey === column.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                {column.label}
                <span className={styles.sortMark}>
                  {sortKey === column.key ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {users.map((user) => {
            const label = userLabel(user);
            return (
              <tr
                key={user.userId}
                className={selectedUserId === user.userId ? styles.selectedRow : ''}
                onClick={() => onSelect(user)}
              >
                <td>
                  <span className={styles.cellId}>{label.idNo}</span>
                  {/* 사번이 없는 사용자는 축약한 userId로 행을 가린다 */}
                  {label.shortId && <span className={styles.cellSub}>{label.shortId}</span>}
                  {isSystemUser(user) && (
                    <span className={styles.systemTag}>사용자 미상 호출 합계</span>
                  )}
                </td>
                <td>{label.dept}</td>
                <td className={styles.num}>{formatNumber(user.totalTokens)}</td>
                <td className={styles.num}>{formatNumber(user.promptTokens)}</td>
                <td className={styles.num}>{formatNumber(user.completionTokens)}</td>
                <td className={styles.num}>{formatNumber(user.requestCount)}</td>
                <td title={formatDateTime(user.lastUsedAt)}>{formatRelativeTime(user.lastUsedAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
